import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./sheet";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./dialog";

/**
 * Example usage of the Radix-based `Sheet` (a side-sliding dialog — the
 * replacement for the Chakra `Drawer`). Shows each side, controlled open
 * state, and — crucially — a nested `Dialog` composing over an open Sheet
 * with no trapFocus/z-index hacks (both are Radix overlays).
 */
const SheetExample: React.FC = () => {
	const [side, setSide] = useState<"top" | "right" | "bottom" | "left">("right");
	const [open, setOpen] = useState(false);
	const [nestedOpen, setNestedOpen] = useState(false);

	const triggerClasses =
		"inline-flex h-9 items-center justify-center rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground hover:bg-brand/90";

	return (
		<div className="max-w-xl space-y-6 p-6">
			<h2 className="mb-2 text-xl font-bold">Sheet Examples</h2>
			<p className="text-sm text-gray-600">
				A Tailwind-styled wrapper over <code>@radix-ui/react-dialog</code> — the new home for
				drawer/sidebar UIs.
			</p>

			{/* Side selector */}
			<div className="space-y-2">
				<h3 className="font-medium">Side</h3>
				<div className="flex flex-wrap gap-2">
					{(["top", "right", "bottom", "left"] as const).map((s) => (
						<button
							key={s}
							onClick={() => setSide(s)}
							className={`inline-flex h-9 items-center justify-center rounded-md border px-4 py-2 text-sm font-medium ${
								side === s
									? "border-brand bg-brand/10 text-brand"
									: "border-input bg-background text-foreground hover:bg-accent"
							}`}
						>
							{s}
						</button>
					))}
				</div>
			</div>

			<button className={triggerClasses} onClick={() => setOpen(true)}>
				Open {side} sheet
			</button>

			<Sheet open={open} onOpenChange={setOpen}>
				<SheetContent side={side}>
					<SheetHeader className="border-b border-border">
						<SheetTitle>Sheet title</SheetTitle>
						<SheetDescription>
							Escape, overlay-click and the close button all dismiss the sheet.
						</SheetDescription>
					</SheetHeader>

					<div className="flex-1 overflow-y-auto p-6">
						<p className="text-sm text-gray-700">
							A nested dialog opened from inside a sheet composes natively — focus moves to the
							dialog and returns to the sheet on close, with no z-index juggling.
						</p>
						<button className={`${triggerClasses} mt-4`} onClick={() => setNestedOpen(true)}>
							Open nested dialog
						</button>
					</div>

					<div className="flex justify-end gap-2 border-t border-border px-6 py-4">
						<button
							className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
							onClick={() => setOpen(false)}
						>
							Close
						</button>
					</div>
				</SheetContent>
			</Sheet>

			<Dialog open={nestedOpen} onOpenChange={setNestedOpen}>
				<DialogContent className="block max-w-[26rem]">
					<DialogTitle className="mb-1 text-base font-medium">Nested dialog</DialogTitle>
					<DialogDescription className="mb-5 text-sm text-gray-600">
						This dialog renders above the open sheet and traps focus correctly.
					</DialogDescription>
					<div className="flex justify-end">
						<button
							className="inline-flex h-9 items-center justify-center rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground hover:bg-brand/90"
							onClick={() => setNestedOpen(false)}
						>
							Got it
						</button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default SheetExample;
